import { vec3 } from 'gl-matrix';
import { PointLight } from '../light/PointLight';
import { SpotLight } from '../light/SpotLight';
import type { Light } from '../light/Light';
import { toRadian } from '../math';
import type { Scene } from '../scene/Scene';
import { composeLightWorldPosition } from './transform';
import type { LightSlotDef, MeshSlotDef, SceneCompositeInstance } from './types';

/**
 * Build a `Light` instance from a composite light-slot definition. Owns the
 * point-vs-spot selection, color × intensity × emit scaling, anchor-socket
 * resolution, and (for spot) direction composition. Shared by:
 *
 *   - the runtime `lightEmitter` template
 *   - the editor preview pane (which builds a static light per slot for the
 *     "see what your light will look like" workflow)
 *
 * Caller is responsible for `scene.lightManager.addLight(...)`. We don't add
 * here so the preview pane can position the light into its own preview scene
 * without dragging in lifecycle assumptions.
 *
 * The initial color is `baseColor * intensity * emit`. `LightFlickerBehaviour`
 * overwrites `light.color` every frame, but seeding with the non-flickered
 * value avoids a single dark frame on first render.
 */
export function buildLightFromSlot(
  slot: LightSlotDef,
  meshSlot: MeshSlotDef,
  placement: SceneCompositeInstance,
  scene: Scene,
  emit: number,
): Light {
  const { position, rotation } = composeLightWorldPosition(placement, slot, meshSlot, scene);
  const k = slot.intensity * emit;
  const color: [number, number, number] = [
    slot.color[0] * k,
    slot.color[1] * k,
    slot.color[2] * k,
  ];
  const shadowMode = slot.shadowMode ?? 'none';

  if (slot.type === 'spot') {
    const inner = slot.innerConeAngle ?? 20;
    const outer = slot.outerConeAngle ?? 30;
    // Reference direction is world-down (0,-1,0); composed with placement +
    // local rotation so authors can aim the spot independently of placement.
    const direction = vec3.transformQuat(vec3.create(), vec3.fromValues(0, -1, 0), rotation);
    return new SpotLight({
      position,
      color,
      direction,
      innerRange: slot.innerRange ?? slot.outerRange * 0.5,
      outerRange: slot.outerRange,
      dotInnerLimit: Math.cos(toRadian(inner)),
      dotOuterLimit: Math.cos(toRadian(outer)),
      shadowMode,
    });
  }

  return new PointLight({
    position,
    color,
    innerRange: slot.innerRange ?? slot.outerRange * 0.5,
    outerRange: slot.outerRange,
    shadowMode,
  });
}
